import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/shared/modules/analytics/services/analytics.service';
import {
  EmailValidator,
  normalizePhoneNumber,
  normalizeVATNumber,
  PhoneValidator,
  VatValidator,
} from '@springbox/validators';
import { SmallBizStoreService } from 'src/app/modules/evc-small-biz/stores/small-biz-store.service';
import { fadeInUp } from '../../../../animations/fly-animations';
import { IModalNavigator } from '../../../../component-lib/components/modal/navigator/modal-navigator.interface';
import { IMultiModalComponent } from '../../../../component-lib/components/modal/navigator/multi-modal-component.interface';
import { ChargingCardOutboundInfoComponent } from '../info/info.component';
import { ChargingCardOutboundFormModel } from './form.model';
import { ChargingCardOutboundFormService } from './form.service';

@Component({
  templateUrl: './form.component.html',
  animations: [fadeInUp('200ms')],
})
export class ChargingCardOutboundFormComponent implements OnInit, IMultiModalComponent {
  public model: ChargingCardOutboundFormModel;
  public modalNavigator: IModalNavigator;

  public form: UntypedFormGroup;
  public formSubmitted = false;

  public canGoBack = true;
  public isLoading = false;

  constructor(
    private pageService: ChargingCardOutboundFormService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private analyticsService: AnalyticsService,
    private store: SmallBizStoreService
  ) {
    this.model = this.pageService.getModel();
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.analytics();
  }

  public submit() {
    this.formSubmitted = true;
    this.analyticsSubmit();
    if (this.form.valid) {
      this.isLoading = true;
      this.analyticsLoading();
      this.pageService.post(this.getFormData()).subscribe(
        (data) => {
          this.pageService.analyticsSuccess();
          this.modalNavigator.close();
          this.store.reset();
          this.router.navigate(['/thank-you-outbound-cards'], { skipLocationChange: true });
        },
        (error) => {
          this.isLoading = false;
          console.log('Error', error);
        }
      );
    }
  }

  public goBack(): void {
    const modalInstance = this.modalNavigator.goToView(
      ChargingCardOutboundInfoComponent
    ) as ChargingCardOutboundInfoComponent;
  }

  public invalid(name: string): boolean {
    return this.form.get(name).invalid && (this.form.get(name).dirty || this.formSubmitted);
  }

  public normalizeVatNumber() {
    const value = this.form.get('vatNumber').value;
    this.form.get('vatNumber').setValue(normalizeVATNumber(value), { onlySelf: true, emitEvent: false });
  }

  public normalizePhoneNumber() {
    const value = this.form.get('telephone').value;
    this.form.get('telephone').setValue(normalizePhoneNumber(value), { onlySelf: true, emitEvent: false });
  }

  private buildForm() {
    return this.fb.group({
      vatNumber: this.fb.control(this.model.vatNumber, [Validators.required, VatValidator.validate]),
      company: this.fb.control(this.model.company, [Validators.required]),
      firstName: this.fb.control(this.model.firstName, [Validators.required]),
      lastName: this.fb.control(this.model.lastName, [Validators.required]),
      telephone: this.fb.control(this.model.telephone, [Validators.required, PhoneValidator.validate]),
      email: this.fb.control(this.model.email, [Validators.required, EmailValidator.validate]),
      alreadyClient: this.fb.control(this.model.alreadyClient, []),
      consent: this.fb.control(this.model.consent, [Validators.requiredTrue]),
    });
  }

  private getFormData(): ChargingCardOutboundFormModel {
    return {
      vatNumber: normalizeVATNumber(this.form.get('vatNumber').value),
      company: this.form.get('company').value,
      firstName: this.form.get('firstName').value,
      lastName: this.form.get('lastName').value,
      telephone: normalizePhoneNumber(this.form.get('telephone').value),
      email: this.form.get('email').value,
      consent: this.form.get('consent').value,
      alreadyClient: this.form.get('alreadyClient').value,
    };
  }

  private analytics(): void {
    this.analyticsService.push({
      event: 'b-to-b-ev-simulator-flow',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'step-outbound-charging-card-contact-form',
      eventLabel: 'contact-details',
    });
  }

  private analyticsSubmit(): void {
    this.analyticsService.push({
      event: 'cta',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'click-cta-outbound-charging-card-contact-form',
      eventLabel: 'submit',
    });
  }

  private analyticsLoading(): void {
    this.analyticsService.push({
      event: 'b-to-b-ev-simulator-flow',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'step-outbound-charging-card-contact-form',
      eventLabel: 'loading-screen',
    });
  }
}
